/* eslint-disable no-console */
// Recuperación de una sola pasada ante fallos al cargar chunks lazy tras un
// despliegue. Cuando Cloudflare Pages reemplaza un despliegue, los chunks
// referenciados por una pestaña ya abierta (hashes viejos) desaparecen. Un
// import() dinámico entonces rechaza con ChunkLoadError / "Loading chunk X
// failed". Como el service worker activo (obsoleto) sirve el index.html viejo
// precacheado para las navegaciones, un location.reload() ingenuo puede entrar
// en bucle. Por eso desregistramos el SW y limpiamos sus caches ANTES de
// recargar, protegido con sessionStorage para que ocurra a lo sumo una vez por
// sesión (evita bucles infinitos si la falla no es por despliegue).

const RELOAD_GUARD_KEY = 'chunkReloadGuard.attempted'

function isChunkLoadError (error) {
    if (!error) return false
    const message = error.message || String(error)
    return (
        (error.name || '') === 'ChunkLoadError' ||
        /Loading( CSS)? chunk [\w-]+ failed/i.test(message) ||
        /Loading chunk .* failed/i.test(message) ||
        /ServiceWorker .* unexpected error/i.test(message) ||
        /__WB_REVISION__/.test(message)
    )
}

// `force: true` -> recarga siempre, saltándose el guard de una sola pasada.
// Es para la recuperación pedida EXPLÍCITAMENTE por el usuario (el botón del
// aviso de nueva versión): ahí no hay riesgo de bucle porque no la gatilla un
// error, y si el guard ya estaba puesto por una falla previa de esta sesión el
// botón se quedaba sin hacer nada.
export async function recoverAndReload ({ force = false } = {}) {
    if (!force) {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
            // Segunda falla en la misma sesión -> NO recargar de nuevo. Se deja que
            // el error aflore (Sentry lo capturará) y se confía en el banner de
            // actualización / recarga manual.
            console.warn('[chunkReloadGuard] Recovery already attempted this session; not reloading again.')
            return
        }
        sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
    }

    try {
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations()
            await Promise.all(regs.map((r) => r.unregister()))
        }
        if (window.caches && caches.keys) {
            const keys = await caches.keys()
            await Promise.all(keys.map((k) => caches.delete(k)))
        }
    } catch (e) {
        console.warn('[chunkReloadGuard] cleanup failed, reloading anyway.', e)
    }

    // Con el SW desregistrado y sus caches limpias, esta recarga va a la red por
    // index.html (Cloudflare lo sirve con max-age=0), obteniendo HTML fresco que
    // referencia los nuevos hashes de chunk.
    window.location.reload()
}

export function installChunkReloadGuard () {
    // 1) Rechazos de import() dinámico que burbujean sin manejar.
    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason)) {
            event.preventDefault()
            recoverAndReload()
        }
    })

    // 2) Algunos navegadores exponen fallos de carga de <script> vía window 'error'.
    window.addEventListener('error', (event) => {
        if (isChunkLoadError(event.error)) {
            recoverAndReload()
        }
    }, true)
}

// Limpia el guard una vez que la app arrancó correctamente, para que un
// despliegue POSTERIOR en la misma sesión larga pueda gatillar otra
// recuperación de una sola pasada.
export function clearChunkReloadGuardOnSuccess () {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
}
